import { RealTimeAction } from '@/_core/fluxstore/types';
import { useSendCommand } from '@/_core/cqrs/use';
import { StorymapState } from '../../types';
import { useStorymapStore } from '../../useModule';
import { ChangeCardStatus as ChangeCardStatusCommand } from '@/api/storymapCardDetails/commands';
import { FlashJustDragged } from '../moveCard/flashJustDragged';
import orderBy from 'lodash/orderBy';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class ChangeSubtaskStatus extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'ChangeSubtaskStatus';

    public constructor(
        public id: string,
        public statusKey: number,
        public trackingOrder: number = null,
        public isDispatchedByDetailsModal: boolean = false
    ) {
        super(ChangeSubtaskStatus.ACTION_TYPE);
    }
}

const middleware = async (state: StorymapState, action: ChangeSubtaskStatus): Promise<boolean> => {
    if (action.isDispatchedByDetailsModal) {
        return true;
    }

    const subtask = state.subtasks.find((x) => x.id === action.id);

    const command = await ChangeCardStatusCommand.create(
        subtask.id,
        subtask.title,
        state.subtaskStatuses.find((x) => x.key === subtask.status).name,
        state.subtaskStatuses.find((x) => x.key === action.statusKey).name,
        state.id,
        action.statusKey,
        action.trackingOrder
    );

    useSendCommand(action, {
        createCommand: () => command,
    });

    return true;
};

const reducer = (state: StorymapState, action: ChangeSubtaskStatus): void => {
    const subtask = state.subtasks.find((x) => x.id === action.id);

    const cardsInStatus = orderBy(
        state.subtasks.filter((x) => x.status == action.statusKey && x.releaseId == subtask.releaseId),
        (x) => x.releaseViewOrder
    );
    if (action.trackingOrder === null) {
        // Check if ANY card has been initialized (non-null releaseViewOrder)
        const hasInitializedCards = cardsInStatus.some(x => x.releaseViewOrder !== null && x.releaseViewOrder !== undefined);
        
        if (hasInitializedCards) {
            // Status has been initialized - continue the sequence
            const nonNullOrders = cardsInStatus
                .filter(x => x.releaseViewOrder !== null && x.releaseViewOrder !== undefined)
                .map(x => x.releaseViewOrder);
            action.trackingOrder = Math.max(...nonNullOrders) + 1;
        } else {
            // Status has never been initialized - preserve null
            action.trackingOrder = null;
        }
    }

    // Only reindex if we have a specific tracking order
    if (action.trackingOrder !== null) {
        cardsInStatus.forEach((card, i) => {
            if (i < action.trackingOrder) {
                card.releaseViewOrder = i;
            } else {
                card.releaseViewOrder = i + 1;
            }
        });
    }

    subtask.releaseViewOrder = action.trackingOrder;

    subtask.status = action.statusKey;
    subtask.lastUpdatedAt = new Date();

    if (!action.isDispatchedByRealTimeCallback) {
        dispatch(new FlashJustDragged());
    }
};

//@ts-ignore
registerMiddleware(ChangeSubtaskStatus.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(ChangeSubtaskStatus.ACTION_TYPE, reducer);
